/**
 * SLIDING BANNER SECTION — sliding-banner-section.js
 * Shopify Custom Section | Wedding / Bridal Store
 *
 * Features:
 * - Fade / Slide / Zoom transitions
 * - Autoplay with pause-on-hover
 * - Arrow navigation
 * - Dot navigation
 * - Thumbnail sync
 * - Animated progress bar
 * - Keyboard accessibility
 * - Touch / swipe support
 * - Shopify editor live-reload support
 */

(function () {
  'use strict';

  /* ─── SLIDER CLASS ─────────────────────────────────────── */
  class SlidingBannerSection {
    constructor(sectionEl) {
      this.section      = sectionEl;
      this.slider       = sectionEl.querySelector('[data-slider]');
      this.slides       = Array.from(sectionEl.querySelectorAll('.sbs-slide'));
      this.dots         = Array.from(sectionEl.querySelectorAll('[data-dot]'));
      this.thumbs       = Array.from(sectionEl.querySelectorAll('[data-thumb]'));
      this.prevBtn      = sectionEl.querySelector('[data-prev]');
      this.nextBtn      = sectionEl.querySelector('[data-next]');
      this.mainEl       = sectionEl.querySelector('.sbs-main');

      this.current      = 0;
      this.total        = this.slides.length;
      this.isAnimating  = false;

      // Settings from data attributes
      this.autoplay     = sectionEl.dataset.autoplay === 'true';
      this.autoplayDelay = parseInt(sectionEl.dataset.autoplaySpeed, 10) || 5000;
      this.transition   = sectionEl.dataset.transition || 'fade';
      this.autoplayTimer = null;

      if (this.total < 2) return; // Single slide — nothing to do

      this._buildCounter();
      this._bindEvents();
      this._updateCounter();

      if (this.autoplay) {
        this._startAutoplay();
        this._bindPauseOnHover();
      }
    }

    /* ── Build a slide counter element ── */
    _buildCounter() {
      const counter = document.createElement('div');
      counter.className = 'sbs-counter';
      counter.innerHTML = `
        <span class="sbs-counter__current">01</span>
        <span style="opacity:0.4">—</span>
        <span class="sbs-counter__total">${String(this.total).padStart(2, '0')}</span>
      `;
      this.mainEl.appendChild(counter);
      this.counterCurrent = counter.querySelector('.sbs-counter__current');
    }

    /* ── Update counter display ── */
    _updateCounter() {
      if (this.counterCurrent) {
        this.counterCurrent.textContent = String(this.current + 1).padStart(2, '0');
      }
    }

    /* ── Bind all events ── */
    _bindEvents() {
      // Arrows
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this._navigate(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this._navigate(1));
      }

      // Dots
      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.dot, 10);
          this._goTo(idx);
        });
      });

      // Thumbnails
      this.thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const idx = parseInt(thumb.dataset.thumb, 10);
          this._goTo(idx);
        });
        // Keyboard: Enter / Space
        thumb.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const idx = parseInt(thumb.dataset.thumb, 10);
            this._goTo(idx);
          }
        });
      });

      // Keyboard on main slider
      this.section.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  this._navigate(-1);
        if (e.key === 'ArrowRight') this._navigate(1);
      });

      // Touch / swipe
      this._bindSwipe();
    }

    /* ── Touch swipe support ── */
    _bindSwipe() {
      let startX = 0;
      let startY = 0;
      let isDragging = false;

      this.slider.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isDragging = true;
      }, { passive: true });

      this.slider.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        const dx = Math.abs(e.touches[0].clientX - startX);
        const dy = Math.abs(e.touches[0].clientY - startY);
        // Suppress default scroll only for horizontal swipes
        if (dx > dy && dx > 8) {
          e.preventDefault();
        }
      }, { passive: false });

      this.slider.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        const endX = e.changedTouches[0].clientX;
        const diff = startX - endX;
        if (Math.abs(diff) > 40) {
          this._navigate(diff > 0 ? 1 : -1);
        }
        isDragging = false;
      }, { passive: true });
    }

    /* ── Navigate relative ── */
    _navigate(dir) {
      const next = (this.current + dir + this.total) % this.total;
      this._goTo(next, dir);
    }

    /* ── Go to a specific slide ── */
    _goTo(idx, dir = null) {
      if (this.isAnimating || idx === this.current) return;

      this.isAnimating = true;

      const prev = this.current;
      const curr = idx;

      // Determine direction for slide transition
      if (dir === null) {
        dir = curr > prev ? 1 : -1;
      }

      const prevSlide = this.slides[prev];
      const currSlide = this.slides[curr];

      // ── Handle slide transition type ──
      if (this.transition === 'slide') {
        // Reset any leftover prev classes
        this.slides.forEach(s => s.classList.remove('is-prev'));

        // Outgoing: slide out to left/right
        prevSlide.classList.add('is-prev');
        prevSlide.classList.remove('is-active');

        // Incoming: start from right/left, then animate in
        currSlide.style.transform = dir > 0 ? 'translateX(100%)' : 'translateX(-100%)';
        currSlide.style.opacity = '1';
        currSlide.offsetWidth; // reflow

        currSlide.classList.add('is-active');
        currSlide.style.transform = '';
        currSlide.style.opacity   = '';

      } else {
        // Fade or Zoom: simple class toggle
        prevSlide.classList.remove('is-active');
        currSlide.classList.add('is-active');
      }

      // ── Update indicators ──
      this._updateDots(curr);
      this._updateThumbs(curr);
      this._updateCounter();

      this.current = curr;

      // ── Reset progress bar ──
      if (this.autoplay && this.mainEl) {
        this.mainEl.classList.remove('is-playing', 'is-reset');
        this.mainEl.classList.add('is-reset');
        requestAnimationFrame(() => {
          this.mainEl.classList.remove('is-reset');
          setTimeout(() => {
            this.mainEl.style.setProperty('--progress-duration', `${this.autoplayDelay}ms`);
            this.mainEl.classList.add('is-playing');
          }, 30);
        });
      }

      // ── Unlock after transition ──
      const transSpeed = parseInt(
        getComputedStyle(this.section).getPropertyValue('--sbs-transition-speed') || '700',
        10
      );
      setTimeout(() => {
        if (this.transition === 'slide') {
          this.slides.forEach(s => s.classList.remove('is-prev'));
        }
        this.isAnimating = false;
      }, transSpeed + 50);
    }

    /* ── Sync dot states ── */
    _updateDots(idx) {
      this.dots.forEach((dot, i) => {
        const active = i === idx;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    /* ── Sync thumbnail states ── */
    _updateThumbs(idx) {
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === idx);
      });
      // Scroll active thumb into view on mobile (horizontal strip)
//      if (window.innerWidth <= 767) {
//   this.thumbs[idx].scrollIntoView({
//     behavior: 'smooth',
//     block: 'nearest',
//     inline: 'center'
//   });
// }
    }

    /* ── Autoplay ── */
    _startAutoplay() {
      this._stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this._navigate(1);
      }, this.autoplayDelay);

      // Start progress bar
      if (this.mainEl) {
        this.mainEl.classList.remove('is-reset');
        this.mainEl.style.setProperty('--progress-duration', `${this.autoplayDelay}ms`);
        setTimeout(() => this.mainEl.classList.add('is-playing'), 50);
      }
    }

    _stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
      if (this.mainEl) {
        this.mainEl.classList.remove('is-playing');
      }
    }

    _bindPauseOnHover() {
      this.section.addEventListener('mouseenter', () => this._stopAutoplay());
      this.section.addEventListener('mouseleave', () => this._startAutoplay());
      // Also pause on focus (keyboard nav)
      this.section.addEventListener('focusin', () => this._stopAutoplay());
      this.section.addEventListener('focusout', (e) => {
        if (!this.section.contains(e.relatedTarget)) {
          this._startAutoplay();
        }
      });
    }

    /* ── Public destroy (for editor re-renders) ── */
    destroy() {
      this._stopAutoplay();
    }
  }

  /* ─── PROGRESS BAR CSS injection ──────────────────────── */
  (function injectProgressBarCSS() {
    const id = 'sbs-progress-style';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      .sbs-main.is-playing::after {
        transition-duration: var(--progress-duration, 5000ms) !important;
        width: 100% !important;
      }
      .sbs-main.is-reset::after {
        transition: none !important;
        width: 0 !important;
      }
    `;
    document.head.appendChild(style);
  })();

  /* ─── INIT ─────────────────────────────────────────────── */
  const instances = new Map();

  function initSection(el) {
    if (instances.has(el)) return;
    const instance = new SlidingBannerSection(el);
    instances.set(el, instance);
  }

  function destroySection(el) {
    if (instances.has(el)) {
      instances.get(el).destroy();
      instances.delete(el);
    }
  }

  /* ── Init on DOM ready ── */
  function initAll() {
    document.querySelectorAll('[id^="sliding-banner-"]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ─── SHOPIFY THEME EDITOR SUPPORT ────────────────────── */
  if (window.Shopify && window.Shopify.designMode) {

    document.addEventListener('shopify:section:load', (e) => {
      const el = e.target.querySelector('[id^="sliding-banner-"]');
      if (el) initSection(el);
    });

    document.addEventListener('shopify:section:unload', (e) => {
      const el = e.target.querySelector('[id^="sliding-banner-"]');
      if (el) destroySection(el);
    });

    document.addEventListener('shopify:section:reorder', initAll);

    // Block select: jump to that slide in editor
    document.addEventListener('shopify:block:select', (e) => {
      const block = e.target;
      const sectionEl = block.closest('[id^="sliding-banner-"]');
      if (!sectionEl || !instances.has(sectionEl)) return;

      const instance = instances.get(sectionEl);
      const idx = instance.slides.indexOf(block);
      if (idx >= 0) instance._goTo(idx);
    });
  }

})();
